import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { trackPageView, trackFAQInteraction, trackCTAClick, trackInternalLink } from '../utils/analytics';

const content = {
  en: {
    // SEO Metadata
    title: 'Where Can I Get Aesthetic Services After Hours on Friday? | Halo Aesthetic Clinic',
    description: 'Looking for aesthetic services after work on Friday? Halo Aesthetic Clinic offers extended hours Friday 1PM-9PM. Book your appointment for injectables, skincare, and body treatments.',
    
    // Main Question (H1)
    mainQuestion: 'Where Can I Get Aesthetic Services After Hours on Friday?',
    
    // Supporting Sections (H2)
    intro: 'Finding Time for Beauty After Work',
    introText: 'We understand that your busy schedule makes it challenging to find time for self-care during regular business hours. That\'s why we offer extended hours every Friday from 1PM to 9PM, making it convenient for you to receive professional aesthetic treatments after work.',
    
    servicesTitle: 'Services Available During After-Hours',
    servicesText: 'All our premium aesthetic services are available during Friday extended hours, including injectable treatments, advanced skincare procedures, and body contouring.',
    
    benefitsTitle: 'Why Choose Friday After-Hours',
    benefitsText: 'Start your weekend feeling refreshed and confident. Friday evening appointments give you time to recover over the weekend if needed.',
    
    bookingTitle: 'Book Your Appointment',
    bookingText: 'Schedule your Friday evening appointment today. Our team is ready to help you achieve your aesthetic goals.',
    
    ctaButton: 'Book Now',
    
    // FAQ Placeholder
    faqTitle: 'Frequently Asked Questions',
    faqPlaceholder: 'FAQ content will be added here with common questions about after-hours aesthetic services, appointment availability, and treatment options.',
  },
  cn: {
    // SEO Metadata
    title: '周五下班后哪里可以做医美服务？| Halo医美中心',
    description: '寻找周五下班后的医美服务？Halo医美中心每周五下午1点至晚上9点提供延长服务时间。预约注射美容、皮肤护理和身体塑形服务。',
    
    // Main Question (H1)
    mainQuestion: '周五下班后哪里可以做医美服务？',
    
    // Supporting Sections (H2)
    intro: '工作之余的美丽时光',
    introText: '我们理解您繁忙的日程安排使得在工作时间内很难找到自我护理的时间。因此，我们每周五提供延长服务时间，从下午1点到晚上9点，让您在下班后也能方便地接受专业医美治疗。',
    
    servicesTitle: '延长服务时间提供的服务',
    servicesText: '周五延长服务时间提供所有优质医美服务，包括注射治疗、高级皮肤护理程序和身体塑形。',
    
    benefitsTitle: '为什么选择周五延长服务时间',
    benefitsText: '以焕然一新的自信开始周末。周五晚上预约让您有充足时间在周末恢复。',
    
    bookingTitle: '立即预约',
    bookingText: '今天就预约您的周五晚上服务。我们的团队随时准备帮助您实现医美目标。',
    
    ctaButton: '立即预约',
    
    // FAQ Placeholder
    faqTitle: '常见问题',
    faqPlaceholder: '常见问题内容将在此添加，包括关于延长服务时间、预约可用性和治疗选项的常见问题。',
  }
};

const AfterHoursServices = () => {
  const { lang } = useLang();
  const c = content[lang];
  const location = useLocation();
  const hasTrackedPageView = useRef(false);

  // 跟踪页面浏览和核心问题展示
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackPageView(location.pathname, c.title, c.mainQuestion);
      hasTrackedPageView.current = true;
    }
  }, [location.pathname, c.title, c.mainQuestion]);

  // FAQ交互追踪处理函数
  const handleFAQToggle = (question, isOpen) => {
    trackFAQInteraction(question, isOpen ? 'expand' : 'collapse', location.pathname);
  };

  // CTA点击追踪处理函数
  const handleCTAClick = () => {
    trackCTAClick(c.ctaButton, 'hero', location.pathname);
  };

  // 内部链接点击追踪处理函数
  const handleInternalLinkClick = (linkText, linkPath) => {
    trackInternalLink(linkText, linkPath, location.pathname);
  };

  // JSON-LD Structured Data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.haloaestheticclinic.com/after-hours-services#webpage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": lang === 'en' 
          ? "Where can I get aesthetic services after work hours on Friday?"
          : "周五下班后哪里可以做医美服务？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": lang === 'en'
            ? "Halo Aesthetic Clinic offers extended hours every Friday from 1PM to 9PM, making it convenient for busy professionals. All our premium services are available during these after-hours appointments."
            : "Halo医美中心每周五提供延长服务时间，从下午1点到晚上9点，方便忙碌的专业人士。我们所有的优质服务在这些延长服务时间内都可提供。"
        }
      },
      {
        "@type": "Question",
        "name": lang === 'en'
          ? "What services are available during Friday after-hours?"
          : "周五延长服务时间提供哪些服务？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": lang === 'en'
            ? "Our Friday extended hours include all our premium aesthetic services, from injectable treatments to advanced skincare procedures and body contouring."
            : "我们的周五延长服务时间包括所有优质医美服务，从注射治疗到高级皮肤护理程序和身体塑形。"
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": "https://www.haloaestheticclinic.com/#organization",
    "name": "Halo Aesthetic Clinic",
    "url": "https://www.haloaestheticclinic.com",
    "image": "https://www.haloaestheticclinic.com/logo.jpg",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.haloaestheticclinic.com/logo.jpg"
    },
    "telephone": "+14377555409",
    "email": "info@haloaestheticclinic.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1595 16th Ave Suite 103",
      "addressLocality": "Richmond Hill",
      "addressRegion": "ON",
      "postalCode": "L4B 3N9",
      "addressCountry": "CA"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Tuesday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Wednesday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Thursday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Friday",
        "opens": "13:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "11:00",
        "closes": "19:00"
      }
    ],
    "priceRange": "$$",
    "areaServed": {
      "@type": "City",
      "name": "Richmond Hill"
    },
    "medicalSpecialty": "Dermatology"
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": c.mainQuestion,
    "description": c.description,
    "author": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "publisher": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "datePublished": "2024-01-01",
    "dateModified": "2024-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.haloaestheticclinic.com/after-hours-services"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": lang === 'en' ? "After-Hours Aesthetic Services" : "延长服务时间医美服务",
    "serviceType": "After-Hours Aesthetic Services",
    "description": lang === 'en'
      ? "Extended hours aesthetic services available every Friday from 1PM to 9PM. Includes injectable treatments, advanced skincare procedures, and body contouring services."
      : "每周五下午1点至晚上9点提供延长服务时间医美服务。包括注射治疗、高级皮肤护理程序和身体塑形服务。",
    "url": "https://www.haloaestheticclinic.com/after-hours-services",
    "provider": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "areaServed": {
      "@type": "City",
      "name": "Richmond Hill"
    },
    "hoursAvailable": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Friday",
      "opens": "13:00",
      "closes": "21:00"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://www.haloaestheticclinic.com/after-hours-services",
      "servicePhone": "+14377555409"
    }
  };

  return (
    <>
      <Helmet>
        <title>{c.title}</title>
        <meta name="description" content={c.description} />
        <link rel="canonical" href="https://www.haloaestheticclinic.com/after-hours-services" />
        <meta name="keywords" content={lang === 'en' 
          ? 'after hours aesthetic services, Friday evening appointments, extended hours clinic, after work beauty treatments'
          : '下班后医美服务, 周五晚上预约, 延长服务时间诊所, 下班后美容治疗'
        } />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>
      
      <div className="bg-softGradient bg-no-repeat bg-top text-text">
        {/* Hero Section with Main Question */}
        <section className="py-16 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              {c.mainQuestion}
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {c.introText}
            </p>
          </div>
        </section>

        {/* Supporting Section 1: Services */}
        <section className="py-16 bg-white/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 text-center">{c.servicesTitle}</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              {c.servicesText}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'Injectable Treatments' : '注射治疗'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en' 
                    ? 'Botox, fillers, and other injectable treatments available during extended hours.'
                    : '肉毒杆菌、填充剂和其他注射治疗在延长服务时间内提供。'
                  }
                </p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'Skincare Procedures' : '皮肤护理程序'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en'
                    ? 'Advanced facials, HydraFacial, and specialized skincare treatments.'
                    : '高级面部护理、水光针和专业皮肤护理治疗。'
                  }
                </p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'Body Contouring' : '身体塑形'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en'
                    ? 'Body shaping treatments and contouring procedures available Friday evenings.'
                    : '身体塑形治疗和轮廓程序在周五晚上提供。'
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Supporting Section 2: Benefits & Booking */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">{c.benefitsTitle}</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {c.benefitsText}
              </p>
              <Link
                to="/book-form"
                onClick={handleCTAClick}
                className="inline-block px-8 py-3 rounded-xl shadow-soft transition-all text-gray-900 hover:shadow-glow hover:brightness-110 border border-champagne/60 text-lg font-semibold"
                style={{
                  backgroundImage:
                    'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
                }}
                aria-label={lang === 'en' ? 'Book your Friday after-hours appointment' : '预约周五延长服务时间'}
              >
                {c.ctaButton}
              </Link>
            </div>
            
            {/* Related Services Section */}
            <div className="mt-16 bg-white/70 backdrop-blur-sm rounded-xl p-8 border border-champagne/40">
              <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">
                {lang === 'en' ? 'Explore More Services' : '探索更多服务'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Link 
                  to="/asian-skin-treatment"
                  onClick={() => handleInternalLinkClick(
                    lang === 'en' ? 'Asian Skin Specialized Treatment' : '亚洲皮肤专业治疗',
                    '/asian-skin-treatment'
                  )}
                  className="block p-6 rounded-lg border border-champagne/40 hover:border-[#2E6B4C] hover:shadow-glow transition-all bg-white/50"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {lang === 'en' ? 'Asian Skin Specialized Treatment' : '亚洲皮肤专业治疗'}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {lang === 'en' 
                      ? 'Specialized treatments designed for Asian skin types.'
                      : '专为亚洲肤质设计的专业治疗。'
                    }
                  </p>
                </Link>
                <Link 
                  to="/ulthera-richmond-hill"
                  onClick={() => handleInternalLinkClick(
                    lang === 'en' ? 'Ulthera Prime Treatment' : 'Ulthera Prime治疗',
                    '/ulthera-richmond-hill'
                  )}
                  className="block p-6 rounded-lg border border-champagne/40 hover:border-[#2E6B4C] hover:shadow-glow transition-all bg-white/50"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {lang === 'en' ? 'Ulthera Prime Treatment' : 'Ulthera Prime治疗'}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {lang === 'en' 
                      ? 'Non-invasive skin tightening in Richmond Hill.'
                      : '在Richmond Hill提供的非侵入性皮肤紧致治疗。'
                    }
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-softRadial">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">{c.faqTitle}</h2>
            <div className="space-y-4">
              <details 
                className="bg-white/70 backdrop-blur-sm rounded-xl border border-champagne/40 overflow-hidden transition-all duration-300 hover:shadow-glow"
                onToggle={(e) => {
                  const question = lang === 'en' ? 'What services are available during Friday after-hours?' : '周五延长服务时间提供哪些服务？';
                  handleFAQToggle(question, e.target.open);
                }}
              >
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 text-lg hover:text-[#2E6B4C] transition-colors list-none">
                  <span className="flex items-center justify-between">
                    <span>{lang === 'en' ? 'What services are available during Friday after-hours?' : '周五延长服务时间提供哪些服务？'}</span>
                    <svg className="w-5 h-5 text-[#2E6B4C] transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-4 pt-2 text-gray-700 leading-relaxed">
                  <p>
                    {lang === 'en' 
                      ? 'Our Friday extended hours include all our premium aesthetic services, from injectable treatments to advanced skincare procedures and body contouring. All services available during regular hours are also available during our extended Friday hours.'
                      : '我们的周五延长服务时间包括所有优质医美服务，从注射治疗到高级皮肤护理程序和身体塑形。常规时间提供的所有服务在周五延长服务时间内也可提供。'
                    }
                  </p>
                </div>
              </details>
              
              <details 
                className="bg-white/70 backdrop-blur-sm rounded-xl border border-champagne/40 overflow-hidden transition-all duration-300 hover:shadow-glow"
                onToggle={(e) => {
                  const question = lang === 'en' ? 'How do I book a Friday after-hours appointment?' : '如何预约周五延长服务时间？';
                  handleFAQToggle(question, e.target.open);
                }}
              >
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 text-lg hover:text-[#2E6B4C] transition-colors list-none">
                  <span className="flex items-center justify-between">
                    <span>{lang === 'en' ? 'How do I book a Friday after-hours appointment?' : '如何预约周五延长服务时间？'}</span>
                    <svg className="w-5 h-5 text-[#2E6B4C] transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-4 pt-2 text-gray-700 leading-relaxed">
                  <p>
                    {lang === 'en' 
                      ? 'You can book your Friday after-hours appointment through our online booking form or by calling us at 437-755-5409. We recommend booking in advance as Friday evening slots are popular.'
                      : '您可以通过我们的在线预约表单或致电437-755-5409预约周五延长服务时间。我们建议提前预约，因为周五晚上的时段很受欢迎。'
                    }
                  </p>
                </div>
              </details>
              
              <div className="text-center mt-6">
                <Link 
                  to="/faq" 
                  className="text-[#2E6B4C] font-semibold hover:underline"
                  aria-label={lang === 'en' ? 'View all frequently asked questions' : '查看所有常见问题'}
                >
                  {lang === 'en' ? 'View All FAQs →' : '查看所有常见问题 →'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AfterHoursServices;

